import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Check, HelpCircle, Search, Users, X } from "lucide-react";
import { useState } from "react";
import { Button } from "~/components/ui/button";
import type { Rsvp } from "~/lib/api/types";
import { cn } from "~/lib/utils";

type GuestListProps = {
	rsvps: Rsvp[];
	userRsvp?: Rsvp;
	isHost?: boolean;
};

export const GuestList = ({ rsvps, userRsvp, isHost }: GuestListProps) => {
	const [searchQuery, setSearchQuery] = useState("");
	const [activeTab, setActiveTab] = useState<"all" | "going" | "maybe">("all");

	// Filter by search query
	const filteredBySearch = rsvps.filter((rsvp) =>
		rsvp.name.toLowerCase().includes(searchQuery.toLowerCase()),
	);

	// Apply tab filter
	const filteredRsvps =
		activeTab === "all"
			? filteredBySearch
			: activeTab === "going"
				? filteredBySearch.filter((r) => r.status === "accepted")
				: filteredBySearch.filter((r) => r.status === "maybe");

	// Guest counts
	const goingCount = rsvps.filter((r) => r.status === "accepted").length;
	const maybeCount = rsvps.filter((r) => r.status === "maybe").length;
	const declinedCount = rsvps.filter((r) => r.status === "declined").length;

	const visible = userRsvp || isHost;

	return (
		<motion.section
			initial={{ opacity: 0, y: 10 }}
			animate={{ opacity: 1, y: 0 }}
			className="rounded-2xl overflow-hidden backdrop-blur-xl bg-primary/10 border border-white/10"
		>
			{/* Header */}
			<div className="p-5 border-b border-white/10">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-3">
						<div className="p-2 rounded-lg bg-accent/[0.07] border border-accent/[0.15]">
							<Users className="w-4 h-4 text-accent" strokeWidth={2} />
						</div>
						<h2 className="text-lg font-medium text-white">Who's Coming</h2>
					</div>

					{visible && (
						<div className="flex items-center gap-2 text-sm">
							<div className="flex items-center gap-1.5 bg-green-500/10 text-green-400 px-2.5 py-1 rounded-full">
								<Check className="w-3 h-3" strokeWidth={2.5} />
								<span className="font-medium">{goingCount}</span>
							</div>

							{maybeCount > 0 && (
								<div className="flex items-center gap-1.5 bg-amber-500/10 text-amber-400 px-2.5 py-1 rounded-full">
									<HelpCircle className="w-3 h-3" strokeWidth={2.5} />
									<span className="font-medium">{maybeCount}</span>
								</div>
							)}

							{declinedCount > 0 && (
								<div className="flex items-center gap-1.5 bg-red-500/10 text-red-400 px-2.5 py-1 rounded-full">
									<X className="w-3 h-3" strokeWidth={2.5} />
									<span className="font-medium">{declinedCount}</span>
								</div>
							)}
						</div>
					)}
				</div>

				{visible && (
					<div className="mt-3 space-y-3">
						{/* Search */}
						<div className="relative">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
							<input
								type="text"
								placeholder="Search guests..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								className="w-full bg-black/20 border border-white/10 rounded-full py-2 pl-8 pr-3 text-xs text-white placeholder:text-white/40 focus:outline-none focus:ring-1 focus:ring-accent/50 transition-all"
							/>
							{searchQuery && (
								<Button
									type="button"
									variant="ghost"
									size="icon"
									onClick={() => setSearchQuery("")}
									className="absolute right-1.5 top-1/2 -translate-y-1/2 h-5 w-5 text-white/40 hover:text-white/60 bg-primary/10 rounded-full border border-white/10 p-0"
								>
									<X className="h-2.5 w-2.5" />
								</Button>
							)}
						</div>

						{/* Tabs */}
						<div className="flex rounded-full bg-black/20 border border-white/10 p-0.5">
							<button
								type="button"
								onClick={() => setActiveTab("all")}
								className={cn(
									"flex-1 py-1.5 px-3 text-xs font-medium rounded-full transition-all",
									activeTab === "all"
										? "bg-accent text-black"
										: "text-white/70 hover:text-white",
								)}
							>
								All
							</button>
							<button
								type="button"
								onClick={() => setActiveTab("going")}
								className={cn(
									"flex-1 py-1.5 px-3 text-xs font-medium rounded-full transition-all",
									activeTab === "going"
										? "bg-green-500 text-black"
										: "text-white/70 hover:text-white",
								)}
							>
								Going
							</button>
							<button
								type="button"
								onClick={() => setActiveTab("maybe")}
								className={cn(
									"flex-1 py-1.5 px-3 text-xs font-medium rounded-full transition-all",
									activeTab === "maybe"
										? "bg-amber-500 text-black"
										: "text-white/70 hover:text-white",
								)}
							>
								Maybe
							</button>
						</div>
					</div>
				)}
			</div>

			{/* Guest List Content with smooth transitions */}
			{visible ? (
				<div className="overflow-hidden">
					<motion.div
						layout
						initial={{ opacity: 1 }}
						animate={{ opacity: 1 }}
						transition={{
							layout: { duration: 0.4, ease: "easeOut" },
						}}
						className="p-4"
					>
						{filteredRsvps.length > 0 ? (
							<div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
								{filteredRsvps.map((rsvp) => (
									<Link
										key={rsvp.id}
										to="/u/$profile"
										params={{ profile: rsvp.username }}
										className="group"
									>
										<motion.div
											initial={{ opacity: 0, scale: 0.9 }}
											animate={{ opacity: 1, scale: 1 }}
											className="flex flex-col items-center"
										>
											<div className="relative w-12 h-12 mb-1.5">
												<div
													className={cn(
														"w-full h-full rounded-full bg-black/20 border border-white/10 flex items-center justify-center transition-transform group-hover:scale-105 duration-300",
														rsvp.status === "declined" && "opacity-75",
													)}
												>
													<img
														src={
															rsvp.avatar ??
															`https://api.dicebear.com/9.x/initials/svg?seed=${rsvp.name}&radius=50&backgroundType=gradientLinear&backgroundColor=f2f2f2,6f88f7&textColor=000000&fontSize=40`
														}
														alt={rsvp.name}
														className="w-full h-full rounded-full object-cover"
														loading="lazy"
													/>
												</div>

												{/* Status indicator - at the bottom as requested */}
												<div className="absolute -right-0.5 -bottom-0.5">
													{rsvp.status === "accepted" && (
														<div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center shadow-sm">
															<Check
																className="w-3 h-3 text-black"
																strokeWidth={3}
															/>
														</div>
													)}
													{rsvp.status === "maybe" && (
														<div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center shadow-sm">
															<HelpCircle
																className="w-3 h-3 text-black"
																strokeWidth={3}
															/>
														</div>
													)}
													{rsvp.status === "declined" && (
														<div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center shadow-sm">
															<X
																className="w-3 h-3 text-black"
																strokeWidth={3}
															/>
														</div>
													)}
												</div>

												{/* Plus One Indicator */}
												{rsvp.bringingPlusOne && (
													<div className="absolute -right-0.5 -top-0.5">
														<div className="bg-accent text-white text-[8px] font-semibold w-4 h-4 rounded-full flex items-center justify-center shadow-sm">
															+1
														</div>
													</div>
												)}
											</div>

											<div className="w-full text-center">
												<p
													className={cn(
														"text-xs font-medium truncate",
														rsvp.status === "declined"
															? "text-white/70"
															: "text-white",
													)}
												>
													{rsvp.name.split(" ")[0]}
												</p>
												{rsvp.bringingPlusOne && rsvp.plusOneName && (
													<p className="text-[10px] text-white/60 truncate mt-0.5">
														with {rsvp.plusOneName.split(" ")[0]}
													</p>
												)}
											</div>
										</motion.div>
									</Link>
								))}
							</div>
						) : (
							<div className="flex flex-col items-center justify-center py-8 text-white/40">
								<Users className="w-8 h-8 mb-2" strokeWidth={1.5} />
								<p className="text-sm font-medium">
									{searchQuery ? "No guests found" : "No guests yet"}
								</p>
								<p className="text-xs mt-0.5">
									{searchQuery
										? "Try a different search term"
										: "Be the first to RSVP!"}
								</p>
							</div>
						)}
					</motion.div>
				</div>
			) : (
				<div className="flex flex-col items-center justify-center text-center py-10 px-4">
					<div className="mb-4 relative">
						<div className="absolute inset-0 bg-accent blur-2xl opacity-10 rounded-full" />
						<div className="relative w-16 h-16 rounded-full bg-black/30 backdrop-blur-md border border-white/10 flex items-center justify-center">
							<Users className="w-8 h-8 text-white/80" strokeWidth={1.5} />
						</div>
					</div>
					<h3 className="text-lg font-medium text-white mb-1">
						Guest List Private
					</h3>
					<p className="text-white/60 max-w-xs mx-auto mb-4 text-sm">
						RSVP to see who's coming
					</p>
				</div>
			)}
		</motion.section>
	);
};
